<template>
  <div class="main-container">
    <div v-if="hasData">
      <div class="table-button-div">
        <a-space>
          <a-button type="primary" @click="showGraph" icon="line-chart" style="margin-right: 12px">
            {{ showChart ? '显示表格' : '趋势图' }}
          </a-button>
          <a-button type="default" @click="exportTableToExcel" icon="download" style="margin-right: 12px">
            导出 Excel
          </a-button>
          <a-button type="default" @click="exportTableToPDF" icon="download">
            导出 PDF
          </a-button>
        </a-space>
      </div>
      <div class="filter-div">
        <slot name="filter"></slot>
        <div>
          主要指标
          <a-tooltip :overlayStyle="{ width: '300px' }">
            <template slot="title">
              <div class="indent">
                <span>系统给出的财务数据取数优先级为</span>
                <span>YTD数据相减（例如Q3数据为1-9月累计减去H1数据）</span>
                <span>其次根据季度内所有月份数据加和得到</span>
                <span>最后取当期数据</span>
              </div>
            </template>
            <a-icon style="color:rgba(0,0,0,0.4);font-size: 14px" type="question-circle" />
          </a-tooltip>
          <a @click="feedbackVisible = true">(指标有异常，请点击这里告诉我们)</a
          >&nbsp;&nbsp;说明:当前数据为对Portfolio上传的财务文件Al抽取+系统校验+人工核对的原始结果,实际对外口径的数据以财务口径为准。
        </div>
        <button v-if="contentContainerVisible" class="cta-button" @click="toggleSidebar">
          AI解读👇
          <span class="new">NEW</span>
        </button>
        <div v-if="currency">币种：{{ currency }}</div>
      </div>

      <div v-if="contentContainerVisible" class="content-container">
        <a-table id="finance-info" class="finance-table" :columns="columns" :data-source="data" :pagination="false" bordered>
          <template v-slot="text">
            <p>{{ text }}</p>
          </template>
        </a-table>

        <!-- 侧边栏 -->
        <transition name="slide" v-if="isSidebarOpen || dataCheck">
          <div>
            <div v-if="isSidebarOpen" class="sidebar">
              <a-icon type="close" class="close-btn" @click="closeSidebar" />
              <div v-if="isLoading" class="sidebar-loading">
                加载中，请稍候...
              </div>
              <div v-html="displayedText"></div>
            </div>
            <div v-if="dataCheck" class="sidebar">
              <!--               关闭按钮-->
              <a-icon type="close" class="close-btn" @click="closeSidebar" />
              <div>
                <div class="data-title">{{ item.quarterOrMonthAndYear }}取数逻辑</div>
                <div v-if="this.item.companyFiles.length > 0" class="data-content">
                  本季度解析使用文件：
                  <li v-for="(item, index) in this.item.companyFiles" :key="index">
                    <a class="file-name" @click="toPdfView(item.fileId, item.taskId, 2)">{{ item.fileName }}</a>
                  </li>
                </div>
                <div class="data-content">
                  <div v-if="this.item.bsCalMethod">资产负债表数据使用算法：{{ this.item.bsCalMethod }}</div>
                  <div v-if="this.item.isCalMethod">利润表数据使用算法：{{ this.item.isCalMethod }}</div>
                  <div v-if="this.item.cfCalMethod">现金流量表数据使用算法：{{ this.item.cfCalMethod }}</div>
                  <div v-if="this.item.checkLog">备注：{{ this.item.checkLog }}</div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
      <!-- 添加图表容器 -->
      <div v-if="echartsContainerVisible" id="echarts-container" class="echarts-container"></div>
    </div>
    <div>
      <a-empty style="margin-top: 25px" :description="'暂无数据'" v-if="hasData === false" />
    </div>
    <feedback v-model="feedbackVisible" type="portfolio 财务指标"></feedback>
    <a-modal :visible.sync="visible" :footer="null" :closable="false" :maskClosable="false" width="400px" :style="steps[currentStep].position">
      <div class="tour-content">
        <div class="step-title">{{ steps[currentStep].title }}</div>
        <div class="step-desc">
          <template v-if="typeof steps[currentStep].desc === 'object'">
            {{ steps[currentStep].desc.text }}
            <a-icon :type="steps[currentStep].desc.icon" style="color: rgba(0,0,0,0.4); font-size: 14px" />
            {{ steps[currentStep].desc.suffix }}
          </template>
          <template v-else>
            {{ steps[currentStep].desc }}
          </template>
        </div>
        <div class="footer">
          <a-button v-if="currentStep > 0" @click="prevStep">上一步</a-button>
          <a-button type="primary" @click="nextStep">
            {{ currentStep === steps.length - 1 ? '完成' : '下一步' }}
          </a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { Component, Vue } from 'vue-property-decorator';
import { getFinanceInfo, getAiSummaryByFinanceInfo, showChartLog } from '@/api/document';
import feedback from '@/components/base/feedback';
import { marked } from 'marked'; // 引入 marked
import * as echarts from 'echarts';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import projectInfo from '@/components/project/projectInfo';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const quarterList = [
  { value: '2025年四季度' },
  { value: '2025年三季度' },
  { value: '2025年二季度' },
  { value: '2025年一季度' },
  { value: '2024年全年' },
  { value: '2024年四季度' },
  { value: '2024年三季度' },
  { value: '2024年二季度' },
  { value: '2024年一季度' },
  { value: '2023年四季度' },
  { value: '2023年三季度' },
  { value: '2023年二季度' },
  { value: '2023年一季度' },
  { value: '2022年四季度' },
  { value: '2022年三季度' },
  { value: '2022年二季度' },
  { value: '2022年一季度' }
];
@Component({
  components: {
    feedback,
    projectInfo
  }
})
class finance extends Vue {
  unit = '';

  units;

  currency = '';

  currencies;

  hasData = false;

  feedbackVisible = false;

  // 定义侧边栏和逐字显示文本的状态
  isSidebarOpen = false;

  // 解释数据来源和计算逻辑
  dataCheck = false;

  isLoading = false;

  fullText = `
# **财务分析**
`;
  displayedText = '';
  textInterval = null; // 逐字显示的时间间隔
  myChart = null;
  showChart = false;
  contentContainerVisible = true;
  echartsContainerVisible = false;

  titleIndex = [0, 9, 19, 26, 33, 39];
  titleName = ['利润表', '资产负债表', '现金流量表', '盈利能力', '盈利质量', '偿还能力'];

  item = null;
  itemTmp = null;
  visible = false;
  currentStep = 0;
  steps = [
    {
      title: '新增功能',
      desc: {
        text: '表头新增',
        icon: 'question-circle',
        suffix: '按钮，点击查看详细取数逻辑。'
      },
      position: { top: '32%', right: '2%' }
    },
    {
      title: '新增功能',
      desc: '包含解析使用文件和数据计算逻辑',
      position: { top: '30%', left: '30%' }
    },
    {
      title: '快速操作',
      desc: '点击文件名称可快速跳转到文件预览页面',
      position: { top: '40%', left: '30%' }
    }
  ];
  // 初始化图表
  initChart() {
    let chartDom = document.getElementById('echarts-container');
    this.myChart = echarts.init(chartDom);
    // 获取 columns除了第一列的所有列的title 并倒序 用于echarts的x轴
    let xtitle = this.columns
      .slice(1)
      .map(item => item.title)
      .reverse();
    // 获取this.data中name为营业总收入 totalOperatingRevenue数据
    let totalOperatingRevenueList = this.data.filter(item => item.name === '营业总收入')[0];
    let netProfitList = this.data.filter(item => item.name === '净利润')[0];
    let cashAndCashEquivalentsList = this.data.filter(item => item.name === '货币资金')[0];
    let netCashFlowFromOperatingActivitiesList = this.data.filter(item => item.name === '经营活动产生的现金流量净额')[0];
    let netCashFlowFromInvestingActivitiesList = this.data.filter(item => item.name === '投资活动产生的现金流量净额')[0];
    let netCashFlowFromFinancingActivitiesList = this.data.filter(item => item.name === '筹资活动产生的现金流量净额')[0];
    let grossProfitMarginList = this.data.filter(item => item.name === '毛利率（%）')[0];
    let netProfitMarginList = this.data.filter(item => item.name === '净利率（%）')[0];
    // 根据xtitle的顺序获取totalOperatingRevenueList中的数据 为空则不处理，不为空则将数据去除逗号
    totalOperatingRevenueList = xtitle.map(item => {
      const value = totalOperatingRevenueList[item];
      return value ? value.replace(/,/g, '') : value;
    });
    netProfitList = xtitle.map(item => {
      const value = netProfitList[item];
      return value ? value.replace(/,/g, '') : value;
    });
    cashAndCashEquivalentsList = xtitle.map(item => {
      const value = cashAndCashEquivalentsList[item];
      return value ? value.replace(/,/g, '') : value;
    });
    netCashFlowFromOperatingActivitiesList = xtitle.map(item => {
      const value = netCashFlowFromOperatingActivitiesList[item];
      return value ? value.replace(/,/g, '') : value;
    });
    netCashFlowFromInvestingActivitiesList = xtitle.map(item => {
      const value = netCashFlowFromInvestingActivitiesList[item];
      return value ? value.replace(/,/g, '') : value;
    });
    netCashFlowFromFinancingActivitiesList = xtitle.map(item => {
      const value = netCashFlowFromFinancingActivitiesList[item];
      return value ? value.replace(/,/g, '') : value;
    });
    grossProfitMarginList = xtitle.map(item => {
      const value = grossProfitMarginList[item];
      return value ? value.replace(/%/g, '') : value;
    });
    netProfitMarginList = xtitle.map(item => {
      const value = netProfitMarginList[item];
      return value ? value.replace(/%/g, '') : value;
    });

    let option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999'
          }
        }
      },
      toolbox: {
        feature: {
          magicType: { show: true, type: ['bar', 'line'] },
          restore: { show: true }
        }
      },
      legend: {
        data: [
          '营业总收入',
          '净利润',
          '货币资金',
          '经营活动产生的现金流量净额',
          '投资活动产生的现金流量净额',
          '筹资活动产生的现金流量净额',
          '毛利率（%）',
          '净利率（%）'
        ],
        selected: {
          营业总收入: true,
          净利润: true,
          货币资金: false,
          经营活动产生的现金流量净额: false,
          投资活动产生的现金流量净额: false,
          筹资活动产生的现金流量净额: false,
          '毛利率（%）': false,
          '净利率（%）': false
        }
      },
      xAxis: [
        {
          type: 'category',
          data: xtitle,
          axisPointer: {
            type: 'shadow'
          }
        }
      ],
      yAxis: [
        {
          type: 'value'
        },
        {
          type: 'value',
          // min: 0,
          // max: 200,
          // interval: 20,
          axisLabel: {
            formatter: '{value}%'
          }
        }
      ],
      series: [
        {
          name: '营业总收入',
          type: 'line',
          data: totalOperatingRevenueList
        },
        {
          name: '净利润',
          type: 'line',
          data: netProfitList
        },
        {
          name: '货币资金',
          type: 'line',
          data: cashAndCashEquivalentsList
        },
        {
          name: '经营活动产生的现金流量净额',
          type: 'line',
          data: netCashFlowFromOperatingActivitiesList
        },
        {
          name: '投资活动产生的现金流量净额',
          type: 'line',
          data: netCashFlowFromInvestingActivitiesList
        },
        {
          name: '筹资活动产生的现金流量净额',
          type: 'line',
          data: netCashFlowFromFinancingActivitiesList
        },
        {
          name: '毛利率（%）',
          type: 'line',
          yAxisIndex: 1,
          tooltip: {
            valueFormatter: function(value) {
              return value + ' %';
            }
          },
          data: grossProfitMarginList
        },
        {
          name: '净利率（%）',
          type: 'line',
          yAxisIndex: 1,
          tooltip: {
            valueFormatter: function(value) {
              return value + ' %';
            }
          },
          data: netProfitMarginList
        }
      ]
    };
    this.myChart.setOption(option);
  }
  handleResize() {
    if (this.myChart) {
      this.myChart.resize();
    }
  }

  toggleSidebar() {
    this.dataCheck = false;
    this.isSidebarOpen = !this.isSidebarOpen;
    this.displayedText = '';

    this.isLoading = true; // 开始加载数据
    if (this.isSidebarOpen) {
      this.getAiSummaryByFinanceInfo();
    } else {
      clearInterval(this.textInterval);
      this.displayedText = '';
      this.isLoading = false;
    }
  }
  closeSidebar() {
    this.isSidebarOpen = false;
    this.dataCheck = false;
    this.item = null;
    clearInterval(this.textInterval);
    this.displayedText = '';
    this.isLoading = false;
  }

  showGraph() {
    this.showChart = !this.showChart; // 显示图表
    if (this.showChart) {
      this.$nextTick(() => {
        // 获取数据
        this.initChart();
      });
      window.addEventListener('resize', this.handleResize);
      this.echartsContainerVisible = true;
      this.contentContainerVisible = false;
      // 发送一个后端请求，用来监控用户点击了图表
      this.showChartLog();
    } else {
      this.myChart.dispose();
      this.myChart = null;
      // 如果要显示表格，隐藏图表容器
      this.echartsContainerVisible = false;
      this.contentContainerVisible = true;
    }
  }
  async showChartLog() {
    try {
      showChartLog({
        companyId: this.$route.query.id
      });
    } catch (error) {
      console.error('showChart.error', error);
    }
  }

  async getAiSummaryByFinanceInfo() {
    // 调用接口获取AI解读的文本
    try {
      let res = await getAiSummaryByFinanceInfo({
        companyId: this.$route.query.id
      });

      if (res.code === 200) {
        this.fullText = res.data;
      } else {
        this.fullText = 'AI解读失败';
      }
    } catch (error) {
      console.error('获取AI解读失败', error);
      this.fullText = '加载失败，请稍后重试';
    } finally {
      // 等待4秒
      setTimeout(() => {
        // 这里放置3秒后需要执行的代码
        this.isLoading = false;
        // 例如，可以在这里调用 showText 方法
        this.showText();
      }, 3000); // 4000 表示4秒
    }
  }

  showText() {
    const htmlContent = marked(this.fullText);
    let index = 0;
    this.textInterval = setInterval(() => {
      if (index < htmlContent.length) {
        this.displayedText = htmlContent.slice(0, index + 10);
        index = index + 10;
      } else {
        clearInterval(this.textInterval);
      }
    }, 50); // 每50ms插入一个字符
  }

  toPdfView(fileId, taskId, type) {
    let fileType = type === 1 ? 'template' : 'finish';
    window.open(window.location.origin + `/pdfview/${fileType}?id=${fileId}&taskId=${taskId}`);
  }

  firstContent = (text, row, index) => {
    if (this.titleName.includes(row.name)) {
      return {
        children: text,
        attrs: {
          colSpan: 10
        }
      };
    }
    return text || '-';
  };

  secContent = (value, row, index) => {
    const obj = {
      children: (value || '-') + this.unit,
      attrs: {
        colSpan: 1,
        rowSpan: 1
      },
      style: {
        backgroundColor: '#ffffff',
        'text-align': 'right'
      }
    };
    if (this.titleName.includes(row.name)) {
      obj.attrs.colSpan = 0;
      obj.attrs.rowSpan = 1;
    }
    return obj;
  };

  exportTableToExcel() {
    const table = document.getElementById('finance-info'); // 替换为你的表格 ID
    const workbook = XLSX.utils.table_to_book(table, { sheet: 'finance-info' });
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, `财务信息.xlsx`);
  }
  exportTableToPDF() {
    const table = document.getElementById('finance-info');
    html2canvas(table).then(canvas => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save('财务信息.pdf');
    });
  }
  columns = [
    {
      title: '指标',
      dataIndex: 'name',
      width: 240,
      customRender: (text, row, index) => {
        if (this.titleName.includes(row.name)) {
          return {
            children: text,
            style: {
              fontWeight: 'bold', // 这会使字体加粗
              backgroundColor: '#f5f5f5', // 设置背景色为浅蓝色
              'text-align': 'left'
            }
          };
        }
        return {
          children: text,
          style: {
            backgroundColor: '#f5f5f5',
            'text-align': 'left'
          }
        };
      }
    }
  ];
  data = [
    {
      key: '1',
      name: '利润表'
    },
    {
      key: '2',
      name: '营业总收入',
      property: 'totalOperatingRevenue',
      needUnitCurrency: true
    },
    {
      key: '3',
      name: '营业成本',
      property: 'costOfGoodsSold',
      needUnitCurrency: true
    },
    {
      key: '4',
      name: '利润总额',
      property: 'totalProfit',
      needUnitCurrency: true
    },
    {
      key: '5',
      name: '净利润',
      property: 'netProfit',
      needUnitCurrency: true
    },
    {
      key: '6',
      name: '销售费用',
      property: 'sellingExpenses',
      needUnitCurrency: true
    },
    {
      key: '7',
      name: '管理费用',
      property: 'administrativeExpenses',
      needUnitCurrency: true
    },
    {
      key: '8',
      name: '研发费用',
      property: 'researchAndDevelopmentExpenses',
      needUnitCurrency: true
    },
    {
      key: '9',
      name: '财务费用',
      property: 'financialExpenses',
      needUnitCurrency: true
    },
    {
      key: '10',
      name: '资产负债表'
    },
    {
      key: '11',
      name: '货币资金',
      property: 'cashAndCashEquivalents',
      needUnitCurrency: true
    },
    {
      key: '12',
      name: '交易性金融资产',
      property: 'tradingFinancialAssets',
      needUnitCurrency: true
    },
    {
      key: '13',
      name: '应收账款',
      property: 'accountsReceivable',
      needUnitCurrency: true
    },
    {
      key: '14',
      name: '其他应收款',
      property: 'otherReceivables',
      needUnitCurrency: true
    },
    {
      key: '15',
      name: '存货',
      property: 'inventories',
      needUnitCurrency: true
    },
    {
      key: '16',
      name: '流动资产合计',
      property: 'totalCurrentAssets',
      needUnitCurrency: true
    },
    {
      key: '17',
      name: '其他流动资产',
      property: 'otherCurrentAssets',
      needUnitCurrency: true
    },
    {
      key: '18',
      name: '固定资产',
      property: 'fixedAssets',
      needUnitCurrency: true
    },
    {
      key: '19',
      name: '非流动资产合计',
      property: 'totalNonCurrentAssets',
      needUnitCurrency: true
    },
    {
      key: '20',
      name: '资产总计',
      property: 'totalAssets',
      needUnitCurrency: true
    },
    {
      key: '21',
      name: '短期借债',
      property: 'shortTermLoans',
      needUnitCurrency: true
    },
    {
      key: '22',
      name: '应付账款',
      property: 'accountsPayable',
      needUnitCurrency: true
    },
    {
      key: '23',
      name: '其他应付款',
      property: 'otherPayables',
      needUnitCurrency: true
    },
    {
      key: '24',
      name: '流动负债合计',
      property: 'totalCurrentLiabilities',
      needUnitCurrency: true
    },
    {
      key: '25',
      name: '长期借款',
      property: 'longTermBorrowings',
      needUnitCurrency: true
    },
    {
      key: '26',
      name: '非流动负债合计',
      property: 'totalNonCurrentLiabilities',
      needUnitCurrency: true
    },
    {
      key: '27',
      name: '负债总计',
      property: 'totalLiabilities',
      needUnitCurrency: true
    },
    {
      key: '28',
      name: '所有者权益',
      property: 'totalShareholdersEquity',
      needUnitCurrency: true
    },
    {
      key: '29',
      name: '现金流量表'
    },
    {
      key: '30',
      name: '支付给职工以及为职工支付的现金',
      property: 'cashPaidToAndOnBehalfOfEmployees',
      needUnitCurrency: true
    },
    {
      key: '31',
      name: '经营活动产生的现金流量净额',
      property: 'netCashFlowFromOperatingActivities',
      needUnitCurrency: true
    },
    {
      key: '32',
      name: '购建固定资产、无形资产和其他长期资产支付的现金',
      property: 'cashPaidForAssetAcquisition',
      needUnitCurrency: true
    },
    {
      key: '33',
      name: '投资活动产生的现金流量净额',
      property: 'netCashFlowFromInvestingActivities',
      needUnitCurrency: true
    },
    {
      key: '34',
      name: '吸收投资收到的现金',
      property: 'cashReceivedFromInvestmentActivities',
      needUnitCurrency: true
    },
    {
      key: '35',
      name: '取得借款收到的现金',
      property: 'cashReceivedFromBorrowings',
      needUnitCurrency: true
    },
    {
      key: '36',
      name: '偿还债务支付的现金',
      property: 'cashPaidForDividendsInterest',
      needUnitCurrency: true
    },
    {
      key: '37',
      name: '分配股利、利润或偿付利息支付的现金',
      property: 'cashPaidForTheRepaymentOfDebts',
      needUnitCurrency: true
    },
    {
      key: '38',
      name: '筹资活动产生的现金流量净额',
      property: 'netCashFlowFromFinancingActivities',
      needUnitCurrency: true
    },
    {
      key: '39',
      name: '汇率变动对现金及现金等价物的影响',
      property: 'effectOfExchangeRateChanges',
      needUnitCurrency: true
    },
    {
      key: '40',
      name: '现金及现金等价物净增加额',
      property: 'netIncreaseInCashAndCashEquivalents',
      needUnitCurrency: true
    },
    {
      key: '41',
      name: '期初现金流量余额',
      property: 'beginningCashAndCashEquivalents',
      needUnitCurrency: true
    },
    {
      key: '42',
      name: '期末现金流量余额',
      property: 'endingCashAndCashEquivalents',
      needUnitCurrency: true
    },
    {
      key: '43',
      name: '盈利能力'
    },
    {
      key: '44',
      name: '毛利率（%）',
      property: 'grossProfitMargin'
    },
    {
      key: '45',
      name: '净利率（%）',
      property: 'netProfitMargin'
    },
    {
      key: '46',
      name: '盈利质量'
    },
    {
      key: '47',
      name: '净利润同比增长率',
      property: 'yoyNetProfit'
    },
    {
      key: '48',
      name: '净利润环比增长率',
      property: 'qoqNetProfit'
    },
    {
      key: '49',
      name: '营业收入同比增长率',
      property: 'yoyTotalOperatingRevenue'
    },
    {
      key: '50',
      name: '营业收入环比增长率',
      property: 'qoqTotalOperatingRevenue'
    },
    {
      key: '51',
      name: '经营现金流/营业收入',
      property: 'cashFlowFromOperatingActivitiesToOperatingIncome'
    },
    {
      key: '52',
      name: '销售费用比率',
      property: 'sellingExpensesRatio'
    },
    {
      key: '53',
      name: '管理费用比率',
      property: 'administrativeExpensesRatio'
    },
    {
      key: '54',
      name: '研发费用比率',
      property: 'researchAndDevelopmentExpensesRatio'
    },
    {
      key: '55',
      name: '财务费用比率',
      property: 'financialExpensesRatio'
    },
    {
      key: '56',
      name: '偿还能力'
    },
    {
      key: '57',
      name: '资产负债率（%）',
      property: 'assetLiabilityRatio'
    },
    {
      key: '58',
      name: '流动负债/总负债（%）',
      property: 'currentLiabilitiesToTotalLiabilities'
    },
    {
      key: '59',
      name: '流动比率',
      property: 'currentRatio'
    }
  ];
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  }
  created() {
    this.init();
  }

  showCheckData(item) {
    this.isSidebarOpen = false;
    clearInterval(this.textInterval);
    if (this.item === item && this.dataCheck) {
      this.dataCheck = !this.dataCheck;
      return;
    }
    this.dataCheck = true;
    this.item = item;
  }

  async init() {
    let res = await getFinanceInfo({
      companyId: this.$route.query.id
    });
    if (res.data.length > 0) {
      this.hasData = true;
      this.visible = localStorage.getItem('hasSeenTour') !== 'true';
    }
    this.units = [...new Set(res.data.map(item => item.unit))];
    this.currencies = [...new Set(res.data.map(item => item.currency))];
    if (this.units.length === 1) {
      this.unit = this.units[0];
    }
    if (this.currencies.length === 1) {
      if (this.currencies[0] === '人民币') {
        this.currency = '人民币(CNY)';
      } else if (this.currencies[0] === '美元') {
        this.currency = '美元(USD)';
      } else {
        this.currency = '未知';
      }
    }
    let rowIndex = 0;
    for (let i = 0; i < quarterList.length; i++) {
      let quarterItem = quarterList[i];
      let itemList = res.data.filter(da => da.quarterOrMonthAndYear === quarterItem['value']);
      if (itemList === undefined || itemList.length === 0) {
        continue;
      }
      let item = itemList[0];
      if (this.itemTmp === null) {
        this.itemTmp = item;
      }

      let index = item.quarterOrMonthAndYear;
      let customRender_tmp = this.secContent;
      if (rowIndex === 0) {
        customRender_tmp = this.firstContent;
      }
      let title = index;
      if (this.currencies.length > 1) {
        if (item.currency === '人民币') {
          title = title + ' (CNY)';
        } else if (item.currency === '美元') {
          title = title + ' (USD)';
        } else {
          title = title + `${item.currency || ''}`;
        }
      }
      let column = {
        title: title,
        dataIndex: index,
        customRender: customRender_tmp
      };
      let step = 'step' + i;
      column.title = () => (
        <div>
          {title}
          <a-tooltip>
            <a-icon ref={{ step }} style="color:rgba(0,0,0,0.4);font-size: 14px" type="question-circle" onClick={() => this.showCheckData(item)} />
          </a-tooltip>
        </div>
      );
      this.columns.push(column);
      for (const i in this.data) {
        let data_i = this.data[i];
        if (data_i['property']) {
          let value = item[data_i['property']];
          if (value) {
            value = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            if (this.units.length > 1 && data_i.needUnitCurrency && item.unit && item.unit !== '元') {
              value = value + ' ' + item.unit;
            }
            data_i[index] = value;
          }
        }
      }
      rowIndex++;
    }
  }
  nextStep() {
    if (this.currentStep < this.steps.length - 1) {
      this.currentStep++;
    } else {
      this.visible = false;
      localStorage.setItem('hasSeenTour', 'true'); // 记录用户已看过引导
    }
    if (this.currentStep === 1) {
      this.showCheckData(this.itemTmp);
    }
  }
  prevStep() {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
    if (this.currentStep === 0) {
      this.showCheckData(null);
    }
  }
}
export default finance;
</script>

<style lang="less" scoped>
.indent span {
  display: block;
  text-indent: 2em; /* 缩进两个字符的宽度 */
}

.indent span:first-child {
  text-indent: 0; /* 第一行不缩进 */
}
.main-container {
  padding: 5px 8px;
  display: flex;
  flex-direction: column;
}
.filter-div {
  position: relative;
  padding: 5px 0px 10px 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  .search-padding {
    padding-left: 20px;
  }
  .currency-select {
    width: 130px;
  }
  .nickName-select {
    width: 140px;
  }
  .sector-select {
    width: 150px;
  }
  .query-btn {
    margin-left: 10px;
  }
}

.content-container {
  display: flex;
}

.finance-table {
  flex-grow: 1; /* 使表格占据剩余空间 */
  overflow-x: auto; /* 如果表格宽度超过父容器，则添加滚动条 */
  /deep/ .ant-table-thead > tr {
    height: 18px;
    background-color: #fafafa;
    line-height: 1 !important;
  }
  /deep/ .ant-table-tbody > tr > td {
    padding: 12px 8px;
    overflow-wrap: break-word;
    font-size: 16px;
    line-height: 1 !important;
    max-height: 12px;
    text-align: right;
  }
  /deep/.ant-table-fixed > thead > tr {
    font-weight: bold;
    height: 18px !important;
  }
  /deep/ .ant-table-thead tr {
    height: 18px !important;
  }

  ///deep/.row {
  //  height: 5px;
  //}

  /deep/ .ant-table-thead > tr > th {
    text-align: center !important;
  }
}
//.ant-table-thead > tr > th {
//  background-color: #f5f5f5; /* 浅蓝色背景 */
//}

.cta-button {
  position: absolute;
  top: -20px;
  right: 138px;
  background: linear-gradient(45deg, #1e90ff, #00bfff); /* 蓝色渐变 */
  color: white;
  padding: 15px 25px;
  border-radius: 25px;
  font-size: 15px;
  font-weight: bold;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 1000; /* 确保按钮位于其他内容之上 */
}

.cta-button:hover {
  background: linear-gradient(45deg, #1c86ee, #1e90ff); /* 浅蓝色渐变 */
  box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.3);
}

//.cta-button::after {
//  padding-left: 10px;
//}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.cta-button {
  animation: pulse 2s infinite;
}
.sidebar {
  width: 500px; /* 固定宽度的侧边栏 */
  padding: 20px;
  background-color: #f7f7f7;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  border-left: 1px solid #ddd;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter,
.slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.sidebar-loading {
  text-align: center;
  padding: 20px;
  background-color: #f7f7f7;
  border-radius: 5px;
  position: relative; /* 为加载动画提供定位上下文 */
}
.sidebar-loading::before {
  content: '';
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #ccc; /* 灰色边框 */
  border-top-color: #333; /* 深色顶部，以显示旋转效果 */
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
.new {
  background-color: #f00;
  color: #fff;
  // margin-left: 5px;
  padding: 1px 3px 1px 3px;
  font-size: 12px;
  border-radius: 2px;
  display: inline-block;
  transform: scale(0.8);
  height: 18px;
  line-height: 18px;
  margin-right: -6px;
}
.echarts-container {
  width: 100%; /* 使容器宽度占据父元素的50% */
  height: 50vh; /* 使容器高度占据视口的100% */
}
.table-button-div {
  margin: 5px 10px;
  overflow: hidden;
  //height: 24px;
  // display: flex;
  // flex-wrap: wrap;
}
/* 将字体放大、加粗并设置为黑色 */
.data-title {
  font-size: 20px; /* 放大字体 */
  font-weight: bold; /* 加粗字体 */
  color: black; /* 设置字体为黑色 */
  margin-bottom: 20px; /* 设置下边距 */
}
.data-content {
  font-size: 16px; /* 设置字体大小 */
  color: #333; /* 设置字体颜色 */
  margin-bottom: 10px; /* 设置下边距 */
}
/* 关闭按钮样式 */
.close-btn {
  font-size: 20px;
  color: rgba(38, 25, 25, 0.74);
  position: relative; /* 绝对定位 */
  right: 10px; /* 设置右边距 */
  cursor: pointer;
}
//.tour-content {
//  display: flex;
//  flex-direction: column;
//  align-items: center;
//  justify-content: center;
//  padding: 20px;
//  text-align: center;
//}

.step-title {
  font-size: 1.25rem; /* 标题字体大小 */
  font-weight: bold; /* 加粗标题 */
  margin-bottom: 10px; /* 标题与描述之间的间距 */
  color: #333; /* 标题颜色 */
}

.step-desc {
  font-size: 1.1rem; /* 描述字体大小 */
  color: rgba(38, 25, 25, 0.7); /* 描述颜色 */
  margin-bottom: 20px; /* 描述与按钮之间的间距 */
  line-height: 1.5; /* 行高，使文本更易读 */
}
</style>
